import { useTranslation } from '@michelin/central-provider';
import type { Bullet } from './StepBody';

interface StepConfig {
  target: string;
  headline: string;
  description: string;
  position: 'top' | 'bottom' | 'left' | 'right' | 'center';
  routeSegment?: string;
  badge?: string;
  eyebrow?: string;
  bullets?: Bullet[];
  callout?: string;
}

const STEP_CONFIGS: StepConfig[] = [
  {
    target: '#tour-step-home',
    headline: 'Welcome to Michelin Services Platform 3.0',
    description: `This quick tour highlights what's new in version 3.0 and where to find key features. What's included in this tour:`,
    position: 'right',
    routeSegment: 'home',
    badge: "\u{1F195} What's New in v3.0",
    bullets: [
      { icon: '\u{1F4CA}', text: 'Updated navigation and layout' },
      { icon: '\u{26A1}', text: 'New dashboard experience' },
      { icon: '\u{1F4CB}', text: 'Faster access to key information' },
      { icon: '\u{1F3C6}', text: 'Platform enhancements and improvements' },
    ],
  },
  {
    target: '#tour-step-interface-toggle',
    headline: 'Toggle Between New & Legacy Interfaces',
    description:
      'Prefer the classic experience? You can switch between the new and legacy interfaces at anytime - right from the header.',
    position: 'bottom',
    badge: "\u{1F195} What's New in v3.0",
    eyebrow: 'Switch Interfaces',
    bullets: [
      { icon: '\u{26A1}', text: 'New interface - redesigned 3.0 experience' },
      { icon: '\u{2699}', text: 'Legacy interface - familiar classic layout' },
      { icon: '\u{1F504}', text: 'Switch instantly and pick up where you left off' },
    ],
    callout:
      'Explore the new experience with confidence - the legacy interface remains available whenever you need it.',
  },
  {
    target: '#tour-step-request-service',
    headline: 'Request a Service Event',
    description: 'Submit a tire or mechanical roadside assistance request in a few guided steps.',
    position: 'right',
    routeSegment: 'request-service',
    badge: "\u{1F195} What's New in v3.0",
    eyebrow: 'Request Service',
    bullets: [
      { icon: '\u{1F464}', text: 'Contact info - requester and driver details' },
      { icon: '\u{1F4CD}', text: 'Asset location - nearest dealer automatically suggested' },
      { icon: '\u{1F69B}', text: 'Asset information - vehicle, tire and axle details' },
      { icon: '\u{1F4B3}', text: 'Billing - authorization and PO number' },
      { icon: '\u{1F4DD}', text: 'Service details - tire or mechanical issue' },
    ],
    callout: 'Redesigned guided form - faster submission and inline validation.',
  },
  {
    target: '#tour-step-livetrack',
    headline: 'Track Every Open Service Event',
    description: 'Track active and recent service events across your fleet in real time.',
    position: 'right',
    routeSegment: 'liveTrack',
    badge: "\u{1F195} What's New in v3.0",
    eyebrow: 'LiveTrack',
    bullets: [
      { icon: '\u{1F4E1}', text: 'Live status updates - from dispatch to completion' },
      { icon: '\u{1F550}', text: 'ETA tracking and delay alerts' },
      { icon: '\u{1F4C4}', text: 'Full case details, notes, and history' },
      { icon: '\u{1F517}', text: 'Jump directly from any event to Request Service' },
    ],
    callout: 'View and track all service events in one place.',
  },
  {
    target: '#tour-step-fleet-business-insights',
    headline: 'Analytics & Reporting',
    description: "Understand your fleet's performance with actionable analytics and reporting.",
    position: 'right',
    eyebrow: 'Fleet Business Insights',
    bullets: [
      { icon: '\u{1F4B0}', text: 'Spend by service type - understand where your budget goes' },
      { icon: '\u{23F1}', text: 'Downtime trend - over time and by location' },
      { icon: '\u{1F4CD}', text: 'Activity by location - which sites need attention' },
      { icon: '\u{1F3EA}', text: 'Dealer performance ratings - compare servicing locations' },
    ],
  },
  {
    target: '#tour-step-tire-preferences',
    headline: 'Configure Tire & Wheel Profiles',
    description: 'Set up tire and wheel profiles for your fleet vehicles.',
    position: 'right',
    routeSegment: 'tire-wheel',
    eyebrow: 'Tire & Wheel Preferences',
    bullets: [
      { icon: '\u{1F6DE}', text: 'Tire profiles - approved sizes, brands, and replacement rules' },
      { icon: '\u{2699}', text: 'Wheel profiles - wheel type and fitment settings' },
      { icon: '\u{1F4CB}', text: 'Replacement priorities - by axle and tire condition' },
      { icon: '\u{1F464}', text: 'Driver instructions - run-flat, inflation, and spare tire guidance' },
    ],
  },
  {
    target: '#tour-step-mechanical-preferences',
    headline: 'Configure Mechanical Service Profiles',
    description: 'Set up the mechanical service profiles for your fleet vehicles.',
    position: 'right',
    routeSegment: 'mechanical',
    eyebrow: 'Mechanical Preferences',
    bullets: [
      { icon: '\u{1F527}', text: 'Service coverage - define which repairs are approved' },
      { icon: '\u{2705}', text: 'Authorization rules - control when manager approval is required' },
      { icon: '\u{1F69B}', text: 'Vehicle assignments - apply profiles by vehicle type' },
    ],
  },
  {
    target: '#tour-step-contacts',
    headline: 'Key Account Contacts',
    description: 'Ensure the right people are receiving the events and making the decisions',
    position: 'right',
    routeSegment: 'contacts',
    eyebrow: 'Contacts',
    bullets: [
      { icon: '\u{1F6A8}', text: 'ERS manager contacts - approve emergency repairs' },
      { icon: '\u{1F4B3}', text: 'PO & Billing contacts - manage purchase orders and invoices' },
      { icon: '\u{1F4B0}', text: 'Approval limits - set authorization thresholds' },
    ],
    callout: 'Easier access for updates',
  },
  {
    target: '#tour-step-my-admin',
    headline: 'Account Administration',
    description: 'A centralized hub for managing users, account settings, and platform configuration.',
    position: 'right',
    routeSegment: 'my-admin',
    badge: "\u{1F195} What's New in v3.0",
    eyebrow: 'My Admin Area',
    bullets: [
      { icon: '\u{1F464}', text: 'User management - roles and access permissions' },
      { icon: '\u{1F3E2}', text: 'Account settings - company details and preferences' },
      { icon: '\u{1F4DC}', text: 'Quick access to important information' },
    ],
    callout: 'Key account administration tools will be available in one place. ... More to come',
  },
];

export function useTranslatedSteps() {
  const { t } = useTranslation();

  return STEP_CONFIGS.map((config) => ({
    ...config,
    headline: t(config.headline),
    description: t(config.description),
    badge: config.badge ? t(config.badge) : undefined,
    eyebrow: config.eyebrow ? t(config.eyebrow) : undefined,
    bullets: config.bullets?.map((bullet) => ({
      icon: bullet.icon,
      text: t(bullet.text),
    })),
    callout: config.callout ? t(config.callout) : undefined,
  }));
}
